import React, { useCallback, useEffect, useState } from "react";
import Styles from "./CompanyDetails.module.css";
import SelectInput from "../../../commons/Inputs/Select/SelectInput";
import Fileupload from "../../../commons/Inputs/FileUpload/Fileupload";
import {
  apiAssignCompanyTrader,
  getAssignedCompanyTraders,
  UnassignCompanyTrader,
} from "../../../Services/GNA/AlterApi";
import {
  ErrorMessage,
  SuccessMessage,
} from "../../../commons/Toast/ErrorMessage";
import { useDispatch, useSelector } from "react-redux";
import { fetchCompanyById } from "../../../Redux/Slices/gnaAdminSlice";
import TraderEntity from "./TraderEntity/TraderEntity";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Modal } from "@mui/material";
import ConfirmationModal from "../../../commons/ConfirmationModal/ConfirmationModal";
import { usePdfPreview } from "../../../hooks/usePdfPreview";

const ENTITY_TYPES = [
  { id: "consumer", name: "Consumer" },
  { id: "generator", name: "Generator" },
];

const emptyForm = {
  entityType: "",
  entity: null,
  trader: null,
  docId: null,
};

const ConfigurationDetails = ({ traders, isTraderOnly }) => {
  const company = useSelector((s) => s?.gnaAdminSlice?.fetchCompanyById?.data);
  const consumers = useSelector(
    (s) => s?.gnaAdminSlice?.fetchConsumersCompanyId?.data,
  );
  const generators = useSelector(
    (s) => s?.gnaAdminSlice?.fetchGeneratorsCompanyId?.data,
  );

  const companyId = company?.companyId;
  const userId = company?.companyAdminUserId;
  const dispatch = useDispatch();

  const { handlePreviewGeneralTerms, PdfPreviewModal } = usePdfPreview();

  // ── table filter state ────────────────────────────────────────────────────
  const [filterType, setFilterType] = useState("");
  const [filterEntity, setFilterEntity] = useState(null);
  const [assignedList, setAssignedList] = useState(null);
  const [loadingList, setLoadingList] = useState(false);

  // ── modal / form state ────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // ── unassign confirm ──────────────────────────────────────────────────────
  const [unassignModal, setUnassignModal] = useState({
    open: false,
    trader: null,
  });
  const [unassigning, setUnassigning] = useState(false);

  // ── fetch assigned traders ────────────────────────────────────────────────
  const fetchAssigned = useCallback(async () => {
    if (!filterEntity || !companyId) {
      setAssignedList(null);
      return;
    }
    setLoadingList(true);
    const params = { companyId };
    if (filterType === "consumer") params.consumerId = filterEntity.id;
    else params.generatorId = filterEntity.id;
    const res = await getAssignedCompanyTraders(params);
    setAssignedList(res?.data?.code === 200 ? res.data.data || [] : []);
    setLoadingList(false);
  }, [filterEntity, filterType, companyId]);

  useEffect(() => {
    fetchAssigned();
  }, [fetchAssigned]);

  // ── entity lists ──────────────────────────────────────────────────────────
  const filterEntityList =
    filterType === "consumer"
      ? (consumers || []).map((e) => ({
          id: e.consumerId,
          name: e.consumerName,
        }))
      : filterType === "generator"
        ? (generators || []).map((e) => ({
            id: e.generatorId,
            name: e.generatorName,
          }))
        : [];

  const formEntityList =
    form.entityType === "consumer"
      ? (consumers || []).map((e) => ({
          id: e.consumerId,
          name: e.consumerName,
        }))
      : form.entityType === "generator"
        ? (generators || []).map((e) => ({
            id: e.generatorId,
            name: e.generatorName,
          }))
        : [];

  const traderList = (traders || []).map((t) => ({
    id: t.userId,
    name: t.fullName,
  }));

  // ── form helpers ──────────────────────────────────────────────────────────
  const setField = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    if (errors[key]) setErrors((p) => ({ ...p, [key]: "" }));
  };

  const openModal = () => {
    setForm(emptyForm);
    setErrors({});
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setForm(emptyForm);
    setErrors({});
  };

  const validate = () => {
    const e = {};
    if (!form.entityType) e.entityType = "Select entity type";
    if (!form.entity) e.entity = "Select an entity";
    if (!form.trader) e.trader = "Select a trader";
    if (!form.docId) e.docId = "Upload NOC document";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const res = await apiAssignCompanyTrader({
      companyId,
      traderUserId: form.trader.id,
      consumerId: form.entityType === "consumer" ? form.entity.id : null,
      generatorId: form.entityType === "generator" ? form.entity.id : null,
      docId: form.docId,
    });
    setSubmitting(false);
    if (res?.data?.code === 200) {
      SuccessMessage(res.data.message || "Trader assigned successfully");
      dispatch(fetchCompanyById(companyId));
      closeModal();
      // auto-apply filter to match the just-submitted entity so table refreshes
      if (
        form.entityType !== filterType ||
        form.entity?.id !== filterEntity?.id
      ) {
        setFilterType(form.entityType);
        setFilterEntity(form.entity);
      } else {
        fetchAssigned();
      }
    } else {
      ErrorMessage(res?.data?.error || "Failed to assign trader");
    }
  };

  const handleUnassign = async () => {
    if (!unassignModal.trader) return;
    setUnassigning(true);
    const t = unassignModal.trader;
    const params = { companyId, traderUserId: t.userId };
    if (filterType === "consumer") params.consumerId = filterEntity?.id;
    else params.generatorId = filterEntity?.id;
    const res = await UnassignCompanyTrader(params);
    setUnassigning(false);
    if (res?.data?.code === 200) {
      SuccessMessage(res.data.message || "Trader unassigned");
      dispatch(fetchCompanyById(companyId));
      setUnassignModal({ open: false, trader: null });
      fetchAssigned();
    } else {
      ErrorMessage(res?.data?.error || "Failed to unassign");
    }
  };

  if (isTraderOnly)
    return <TraderEntity companyId={companyId} userId={userId} />;

  return (
    <>
      <PdfPreviewModal />

      {/* ── Unassign confirmation ── */}
      {unassignModal.open && (
        <ConfirmationModal
          open={unassignModal.open}
          handleClose={() => setUnassignModal({ open: false, trader: null })}
          header="Unassign Trader"
          data={`Remove ${unassignModal.trader?.fullName || "this trader"} from the selected entity?`}
          handleSubmit={handleUnassign}
          loading={unassigning}
        />
      )}

      {/* ── Assign Trader Modal ── */}
      <Modal open={modalOpen} onClose={closeModal}>
        <div className={Styles.cfgModal}>
          {/* Modal header */}
          <div className={Styles.cfgModalHeader}>
            <span className={Styles.cfgModalTitle}>Assign Trader</span>
            <button className={Styles.cfgModalClose} onClick={closeModal}>
              <CloseIcon style={{ fontSize: "16px" }} />
            </button>
          </div>

          {/* Modal body — steps */}
          <div className={Styles.cfgModalBody}>
            {/* Step 1 — Entity type */}
            <div className={Styles.cfgStep}>
              <div className={Styles.cfgStepHeader}>
                <span className={Styles.cfgStepNum}>1</span>
                <span className={Styles.cfgStepLabel}>Select Entity Type</span>
              </div>
              <div className={Styles.cfgTypeToggle}>
                {ENTITY_TYPES.map((t) => (
                  <button
                    key={t.id}
                    className={`${Styles.cfgTypeBtn} ${form.entityType === t.id ? Styles.cfgTypeBtnActive : ""}`}
                    onClick={() =>
                      setForm((p) => ({
                        ...p,
                        entityType: t.id,
                        entity: null,
                        trader: null,
                        docId: null,
                      }))
                    }
                  >
                    {t.name}
                  </button>
                ))}
              </div>
              {errors.entityType && (
                <span className={Styles.cfgError}>{errors.entityType}</span>
              )}
            </div>

            {/* Step 2 — Entity */}
            <div className={Styles.cfgStep}>
              <div className={Styles.cfgStepHeader}>
                <span
                  className={`${Styles.cfgStepNum} ${!form.entityType ? Styles.cfgStepNumDim : ""}`}
                >
                  2
                </span>
                <span className={Styles.cfgStepLabel}>
                  Select{" "}
                  {form.entityType === "consumer"
                    ? "Consumer"
                    : form.entityType === "generator"
                      ? "Generator"
                      : "Consumer / Generator"}
                </span>
              </div>
              <SelectInput
                list={formEntityList}
                value={form.entity?.id || ""}
                onChange={(val) => setField("entity", val)}
                example="Select entity…"
                height={36}
                disabled={!form.entityType}
              />
              {errors.entity && (
                <span className={Styles.cfgError}>{errors.entity}</span>
              )}
            </div>

            {/* Step 3 — Trader */}
            <div className={Styles.cfgStep}>
              <div className={Styles.cfgStepHeader}>
                <span
                  className={`${Styles.cfgStepNum} ${!form.entity ? Styles.cfgStepNumDim : ""}`}
                >
                  3
                </span>
                <span className={Styles.cfgStepLabel}>Select Trader</span>
              </div>
              <SelectInput
                list={traderList}
                value={form.trader?.id || ""}
                onChange={(val) => setField("trader", val)}
                example="Select trader…"
                height={36}
                disabled={!form.entity}
              />
              {errors.trader && (
                <span className={Styles.cfgError}>{errors.trader}</span>
              )}
            </div>

            {/* Step 4 — NOC Doc */}
            <div className={Styles.cfgStep}>
              <div className={Styles.cfgStepHeader}>
                <span
                  className={`${Styles.cfgStepNum} ${!form.trader ? Styles.cfgStepNumDim : ""}`}
                >
                  4
                </span>
                <span className={Styles.cfgStepLabel}>
                  Upload NOC Document (PDF)
                </span>
              </div>
              <Fileupload
                value={form.docId}
                onChange={({ id }) => setField("docId", id)}
                field="nocDoc"
                id="cfg-noc-doc"
                accept=".pdf"
                height={36}
                example="Upload PDF…"
                disabled={!form.trader}
                error={errors.docId}
              />
            </div>
          </div>

          {/* Modal footer */}
          <div className={Styles.cfgModalFooter}>
            <button
              className={Styles.cfgCancelBtn}
              onClick={closeModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              className={Styles.cfgSubmitBtn}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "Assigning…" : "Assign Trader"}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Main section ── */}
      <div className={Styles.entityDetailSections}>
        <div className={Styles.secCard}>
          {/* Card header: filter + button */}
          <div className={Styles.cfgTableHeader}>
            <div className={Styles.cfgFilters}>
              {/* Entity type toggle */}
              <div className={Styles.cfgTypeToggle}>
                {ENTITY_TYPES.map((t) => (
                  <button
                    key={t.id}
                    className={`${Styles.cfgTypeBtn} ${filterType === t.id ? Styles.cfgTypeBtnActive : ""}`}
                    onClick={() => {
                      setFilterType(t.id);
                      setFilterEntity(null);
                    }}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
              {/* Entity dropdown */}
              <div style={{ width: "200px" }}>
                <SelectInput
                  list={filterEntityList}
                  value={filterEntity?.id || ""}
                  onChange={(val) => setFilterEntity(val)}
                  example={
                    filterType ? `Select ${filterType}…` : "Select type first…"
                  }
                  height={34}
                  disabled={!filterType}
                />
              </div>
            </div>
            <button className={Styles.cfgAssignBtn} onClick={openModal}>
              <AddIcon style={{ fontSize: "15px" }} />
              Assign Trader
            </button>
          </div>

          {/* Table */}
          {!filterEntity ? (
            <div className={Styles.listingEmpty}>
              Select an entity type and entity above to view assigned traders
            </div>
          ) : loadingList ? (
            <div className={Styles.listingEmpty}>Loading…</div>
          ) : !assignedList || assignedList.length === 0 ? (
            <div className={Styles.listingEmpty}>
              No traders assigned to this entity
            </div>
          ) : (
            <>
              <div className={Styles.ltHead} style={{ marginTop: "14px" }}>
                <div style={{ flex: "0.5" }} className={Styles.ltHeadCell}>
                  #
                </div>
                <div style={{ flex: "3" }} className={Styles.ltHeadCell}>
                  Trader Name
                </div>
                <div style={{ flex: "2" }} className={Styles.ltHeadCell}>
                  User ID
                </div>
                <div
                  style={{ flex: "1" }}
                  className={`${Styles.ltHeadCell} ${Styles.ltCenter}`}
                >
                  Document
                </div>
                <div
                  style={{ flex: "1" }}
                  className={`${Styles.ltHeadCell} ${Styles.ltCenter}`}
                >
                  Action
                </div>
              </div>
              {assignedList.map((t, i) => (
                <div key={t.userId || i} className={Styles.ltRow}>
                  <div style={{ flex: "0.5" }} className={Styles.ltCell}>
                    {i + 1}
                  </div>
                  <div style={{ flex: "3" }} className={Styles.ltCell}>
                    {t.fullName || t.traderName || "—"}
                  </div>
                  <div
                    style={{ flex: "2" }}
                    className={`${Styles.ltCell} ${Styles.fieldMono}`}
                  >
                    {t.userId || "—"}
                  </div>
                  <div
                    style={{ flex: "1" }}
                    className={`${Styles.ltCell} ${Styles.ltCenter}`}
                  >
                    {t.traderDocId ? (
                      <button
                        className={Styles.cfgUnassignBtn}
                        title="View NOC document"
                        onClick={() =>
                          handlePreviewGeneralTerms(t.traderDocId, {
                            title: "NOC Document",
                          })
                        }
                      >
                        <VisibilityIcon
                          style={{ fontSize: "14px", color: "var(--primary-color-text)" }}
                        />
                      </button>
                    ) : (
                      "—"
                    )}
                  </div>
                  <div
                    style={{ flex: "1" }}
                    className={`${Styles.ltCell} ${Styles.ltCenter}`}
                  >
                    <button
                      className={Styles.cfgUnassignBtn}
                      title="Unassign trader"
                      onClick={() =>
                        setUnassignModal({ open: true, trader: t })
                      }
                    >
                      <DeleteIcon style={{ fontSize: "14px" }} />
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ConfigurationDetails;
